import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Card,
  CardContent,
  FormControlLabel,
  Checkbox
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import GetAppIcon from '@material-ui/icons/GetApp';
import DeleteIcon from '@material-ui/icons/Delete';
import { solicitudDetalleService } from '../../services';

const DEFAULT_AUTH_API_URL = "http://localhost:8080";

const getAuthApiUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return ''; // Use relative path for proxy in development
  }
  const raw =
    (typeof window !== 'undefined' && window.env?.REACT_APP_AUTH_API_URL) ||
    (typeof window !== 'undefined' && window.env?.REACT_APP_API_URL) ||
    process.env.REACT_APP_AUTH_API_URL ||
    process.env.REACT_APP_API_URL ||
    DEFAULT_AUTH_API_URL;
  return raw.endsWith("/") ? raw.slice(0, -1) : raw;
};

const AUTH_API_URL = getAuthApiUrl();

const SeccionDeclaracionIngresos = ({ solicitudId }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [formData, setFormData] = useState({
    ddJjOrigenFondos: [],
    comprobanteDdJjOrigenFondosId: null
  });

  const cargarArchivoComprobante = async (archivoId) => {
    if (!archivoId) return;
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${AUTH_API_URL}/api/archivos/${archivoId}`, {
        method: 'GET',
        headers: {
          'Accept': '*/*',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        
        // Convertir archivoData (base64) a blob
        if (result.archivoData) {
          const binaryString = atob(result.archivoData);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes]);
          const fileUrl = URL.createObjectURL(blob);
          
          setUploadedFile({
            id: archivoId,
            nombre: result.nombre || 'Comprobante',
            size: blob.size || result.tamanio || 0,
            url: fileUrl
          });
        }
      }
    } catch (err) {
      console.error('Error cargando archivo comprobante:', err);
    }
  };

  const OPCIONES_ORIGEN_FONDOS = [
    { value: 'TRANSFERENCIA_BANCARIA', label: 'Transferencia Bancaria a nombre del titular' },
    { value: 'TRANSFERENCIA_ESPECIES', label: 'Transferencia de Especies' },
    { value: 'CHEQUE', label: 'Entrega de Cheques a nombre del titular' }
  ];

  const cargarDatos = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await solicitudDetalleService.getDeclaracionIngresosIndividuo(solicitudId);
      if (response) {
        setFormData({
          ddJjOrigenFondos: response.ddJjOrigenFondos || [],
          comprobanteDdJjOrigenFondosId: response.comprobanteDdJjOrigenFondosId || null
        });
        
        // Cargar archivo completo si existe
        setUploadedFile(null);
        if (response.comprobanteDdJjOrigenFondosId) {
          await cargarArchivoComprobante(response.comprobanteDdJjOrigenFondosId);
        }
      }
    } catch (err) {
      setError('Error al cargar la declaración de ingresos');
    } finally {
      setLoading(false);
    }
  }, [solicitudId]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const handleCheckboxChange = (value, checked) => {
    setFormData(prev => {
      const currentArray = prev.ddJjOrigenFondos || [];
      if (checked) {
        return {
          ...prev,
          ddJjOrigenFondos: [...currentArray, value]
        };
      } else {
        return {
          ...prev,
          ddJjOrigenFondos: currentArray.filter(item => item !== value)
        };
      }
    });
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar formato
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      setError('Formato no permitido. Solo se aceptan JPG, PNG o PDF.');
      event.target.value = null;
      return;
    }

    // Validar tamaño (10 MB)
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
    if (file.size > MAX_FILE_SIZE) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      setError(`El archivo excede el tamaño máximo permitido de 10 MB. Tamaño actual: ${fileSizeMB} MB.`);
      event.target.value = null;
      return;
    }

    setUploadingFile(true);
    setError('');

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await solicitudDetalleService.uploadArchivo(formDataUpload);
      
      if (response && response.id) {
        setFormData(prev => ({
          ...prev,
          comprobanteDdJjOrigenFondosId: response.id
        }));
        
        // Cargar el archivo completo para obtener la URL
        await cargarArchivoComprobante(response.id);
      }
    } catch (err) {
      console.error('Error subiendo archivo:', err);
      setError('Error al subir el archivo. Intente nuevamente.');
    } finally {
      setUploadingFile(false);
      event.target.value = null;
    }
  };

  const handleRemoveFile = () => {
    // Limpiar la URL del blob para liberar memoria
    if (uploadedFile && uploadedFile.url) {
      URL.revokeObjectURL(uploadedFile.url);
    }
    setFormData(prev => ({
      ...prev,
      comprobanteDdJjOrigenFondosId: null
    }));
    setUploadedFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const datosParaGuardar = {
        solicitudId: parseInt(solicitudId),
        ddJjOrigenFondos: formData.ddJjOrigenFondos,
        comprobanteDdJjOrigenFondosId: formData.comprobanteDdJjOrigenFondosId
      };
      await solicitudDetalleService.saveDeclaracionIngresosIndividuo(datosParaGuardar);
      setSuccess('Datos guardados exitosamente');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error al guardar los datos');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Declaración de Ingresos
        </Typography>
        {error && (
          <Box p={2} mb={2} style={{ backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px' }}>
            <Typography variant="body2">{error}</Typography>
          </Box>
        )}
        {success && (
          <Box p={2} mb={2} style={{ backgroundColor: '#e8f5e9', color: '#2e7d32', borderRadius: '4px' }}>
            <Typography variant="body2">{success}</Typography>
          </Box>
        )}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body1" style={{ fontWeight: 'bold', marginBottom: '16px' }}>
                Declaro bajo juramento que los fondos y/o valores depositados en la Cuenta Comitente en ST SECURITIES S.A.U. se acreditarán a través de:
              </Typography>
            </Grid>
            
            {OPCIONES_ORIGEN_FONDOS.map((opcion) => (
              <Grid item xs={12} key={opcion.value}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.ddJjOrigenFondos.includes(opcion.value)}
                      onChange={(e) => handleCheckboxChange(opcion.value, e.target.checked)}
                    />
                  }
                  label={opcion.label}
                />
              </Grid>
            ))}

            <Grid item xs={12}>
              <Typography variant="body2" style={{ marginTop: '16px', marginBottom: '8px' }}>
                Y tiene su origen en la documentación adjunta:
              </Typography>
            </Grid>

            {uploadedFile ? (
              <Grid item xs={12}>
                <input
                  id="comprobante-upload"
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                  disabled={uploadingFile || saving}
                />
                <Card style={{ border: '1px solid #e0e0e0', backgroundColor: '#f9fafb' }}>
                  <CardContent>
                    <Box display="flex" flexDirection="column" alignItems="flex-start" style={{ gap: '8px' }}>
                      <Typography variant="h6" style={{ fontWeight: 'bold' }}>
                        Comprobante de Origen de Fondos
                      </Typography>
                      <Box display="flex" flexDirection="column" marginTop={1}>
                        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                          {uploadedFile.nombre}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {Math.round((uploadedFile.size || 0) / 1024)} KB
                        </Typography>
                        {uploadedFile.url && (
                          <Box display="flex" style={{ gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
                            <Button
                              variant="text"
                              color="primary"
                              onClick={() => window.open(uploadedFile.url, '_blank')}
                              size="small"
                            >
                              Ver documento
                            </Button>
                            <Button
                              variant="text"
                              color="primary"
                              startIcon={<GetAppIcon />}
                              onClick={() => {
                                const link = document.createElement('a');
                                link.href = uploadedFile.url;
                                link.download = uploadedFile.nombre || 'comprobante.pdf';
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                              }}
                              size="small"
                            >
                              Descargar
                            </Button>
                            <Button
                              variant="outlined"
                              color="primary"
                              startIcon={<CloudUploadIcon />}
                              onClick={() => document.getElementById('comprobante-upload').click()}
                              disabled={uploadingFile || saving}
                              size="small"
                            >
                              Reemplazar
                            </Button>
                            <Button
                              variant="text"
                              color="secondary"
                              startIcon={<DeleteIcon />}
                              onClick={handleRemoveFile}
                              disabled={saving}
                              size="small"
                            >
                              Eliminar
                            </Button>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <input
                  id="comprobante-upload"
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                  disabled={uploadingFile || saving}
                />
                <Box
                  p={3}
                  style={{
                    border: '2px dashed #bdbdbd',
                    borderRadius: '4px',
                    textAlign: 'center',
                    cursor: uploadingFile || saving ? 'not-allowed' : 'pointer',
                    backgroundColor: uploadingFile ? '#f5f5f5' : 'transparent',
                    position: 'relative'
                  }}
                  onClick={() => {
                    if (!uploadingFile && !saving) {
                      document.getElementById('comprobante-upload').click();
                    }
                  }}
                >
                  {uploadingFile ? (
                    <Box display="flex" flexDirection="column" alignItems="center" style={{ gap: '8px' }}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" color="textSecondary">
                        Subiendo archivo...
                      </Typography>
                    </Box>
                  ) : (
                    <Box display="flex" flexDirection="column" alignItems="center" style={{ gap: '8px' }}>
                      <CloudUploadIcon style={{ fontSize: 48, color: '#757575' }} />
                      <Typography variant="body2" style={{ fontWeight: 'bold' }}>
                        SUBIR COMPROBANTE (OPCIONAL)
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: '8px' }}>
                Formatos permitidos: JPG, PNG, PDF. En móvil se abrirá la cámara.
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block">
                Límite de tamaño: 10MB por archivo
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
              >
                {saving ? <CircularProgress size={20} /> : 'Guardar'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </CardContent>
    </Card>
  );
};

export default SeccionDeclaracionIngresos;

